import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  NumberFormatter,
  Paper,
  Stack,
  Text,
  Title,
  useComputedColorScheme,
} from "@mantine/core";
import { checkoutCredits, checkoutSkuPack } from "../api";
import mixpanel from "mixpanel-browser";
import Faq from "./Faq";
import { useStore } from "../store";
import { useMarketplaces } from "../_Shared/useMarketplaces";
import { destinationName, skusByKind } from "../utils/skus";
import {
  OFFER_PACK_SIZES,
  PUBLIC_PACK_CHECKOUT_IDS,
  publicPackInCurrency,
  skuOfferFromProfile,
} from "../utils/skuOffer";

function startCheckout(promise, callback) {
  promise
    .then((data) => {
      try {
        mixpanel.track("Checkout credits");
      } catch {
        /* tracking not initialised — ignore */
      }
      if (callback) callback();
      location.href = data.redirectUrl;
    })
    .catch((error) => {
      alert("Sorry, something went wrong: " + error);
      if (callback) callback();
    });
}

function PlanBox({ plan, highlight, onBuy, style }) {
  const colorScheme = useComputedColorScheme();
  const [isLoading, setIsLoading] = useState(false);

  const baseStyle = {
    boxShadow: "0px 20px 30px -5px rgba(0,0,0,0.1)",
    background:
      colorScheme === "light"
        ? "hsl(228, 7.00%, 95.90%)"
        : "hsl(228, 7.00%, 19.90%)",
    minHeight: "22rem",
    minWidth: "17rem",
    padding: "1.5rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "@media (maxWidth: $mantineBreakpointMd)": {
      width: "19rem",
      borderRadius: "0.7rem",
    },
    "@media (minWidth: $mantineBreakpointMd)": {
      width: "15rem",
    },
  };
  const highlightStyle = {
    ...baseStyle,
    minHeight: "27rem",
    minWidth: "19rem",
    padding: "4.0rem 2.0rem",
    background:
      colorScheme === "light"
        ? "linear-gradient(to bottom right, hsl(231, 100.00%, 94.90%), hsl(228, 38.90%, 92.40%))"
        : "linear-gradient(to bottom right, hsl(231, 7.00%, 20.90%), hsl(228, 7.00%, 13.90%))",
    borderRadius: "0.7rem",
    border: "2px solid  hsl(228, 38.90%, 72.40%)",
    "@media (minWidth: $mantinBreakpointMd)": {
      width: "15rem",
      borderRadius: "0.7rem",
    },
  };

  return (
    <Box style={highlight ? highlightStyle : { ...baseStyle, ...style }}>
      <Stack w="100%" align="center" spacing="xs">
        <Text fw={700} fz="xl">
          {plan.title}
        </Text>

        {plan.strikePrice != null &&
          plan.strikeMark != null &&
          plan.strikeMark === plan.currencyMark && (
          <Text
            fz="xl"
            c="dimmed"
            td="line-through"
            style={{ display: "flex", alignItems: "center", gap: 4 }}
          >
            <Text span fz="lg" c="dimmed">
              {plan.strikeMark}
            </Text>
            <NumberFormatter value={plan.strikePrice} thousandSeparator />
          </Text>
        )}
        <Title
          order={2}
          style={{
            fontSize: 50,
            display: "flex",
            alignItems: "center",
            gap: 5,
          }}
        >
          <Text fz={"2rem"}>{plan.currencyMark}</Text>
          <NumberFormatter value={plan.price} thousandSeparator />
        </Title>

        <Stack w="100%" align="center" gap={10}>
          {plan.features.map((item) => (
            <React.Fragment key={item.id}>
              <Divider
                style={{
                  width: "100%",
                  borderColor: colorScheme === "dark" && "gray",
                  opacity: colorScheme === "dark" && 0.7,
                }}
              />
              <Text fz={"sm"} fw={600}>
                {item.text}
              </Text>
            </React.Fragment>
          ))}
        </Stack>
        <Button
          loading={isLoading}
          color="indigo"
          mt={highlight ? "xl" : "xs"}
          variant={highlight ? "filled" : "outline"}
          size="lg"
          onClick={() => {
            setIsLoading(true);
            onBuy(() => setIsLoading(false));
          }}
          fullWidth
        >
          Buy
        </Button>
      </Stack>
    </Box>
  );
}

function OfferPacks({ offer }) {
  const tenantProfile = useStore((state) => state.tenantProfile);
  const { marketplaces } = useMarketplaces();
  const showFirstPurchaseBonus = !tenantProfile?.firstPurchaseBonusGranted;
  const partnerName =
    tenantProfile?.partnerName ||
    marketplaces?.find((m) => m.value === tenantProfile?.partnerMarketplaceId)
      ?.label;
  const partnerLabel = partnerName || "a marketplace partner";
  const dest = destinationName(tenantProfile, marketplaces);
  const { sponsored, paid } = skusByKind(tenantProfile?.creditTotals);
  const isPartner = Boolean(tenantProfile?.partnerMarketplaceId);
  const rateLine = offer.volumeLadder
    ? ` Usual packs get cheaper as they get larger — ${offer.volumeLadder} — and this rate is still lower.`
    : "";
  const intro = isPartner
    ? `Because ${partnerLabel} introduced you to Optivise, you pay a flat ${offer.unitCopy}.${rateLine}`
    : `You pay a flat ${offer.unitCopy}.${rateLine}`;

  const packs = OFFER_PACK_SIZES.map((skuCount) => {
    const price = offer.packPrice(skuCount);
    const list = offer.publicPack(skuCount);
    const strike =
      list && list.mark === offer.mark && list.major > price ? list : null;
    return {
      title: `${skuCount.toLocaleString()} SKUs`,
      skuCount,
      price,
      currencyMark: offer.mark,
      strikePrice: strike?.major,
      strikeMark: strike?.mark,
      features: [
        {
          id: 1,
          text: list
            ? `${offer.unitCopy} (usually ${list.perSku})`
            : offer.unitCopy,
        },
      ],
    };
  });

  const caption = [
    sponsored > 0
      ? `You have ${sponsored.toLocaleString()} trial SKU${
          sponsored === 1 ? "" : "s"
        } from ${dest || partnerLabel}. They only pay for ${
          dest || "that marketplace"
        } listings.${
          paid > 0
            ? ` ${paid.toLocaleString()} paid SKUs work on any marketplace.`
            : ""
        }`
      : null,
    showFirstPurchaseBonus
      ? "100 extra SKUs are added after your first successful payment — not on signup. A 90-SKU catalogue still buys the 100 pack."
      : null,
  ].filter(Boolean);

  return (
    <Container size="xl">
      <Stack spacing={40} mt="md" mb="xl" pt="md" pb="xl">
        <Flex direction="column" gap={10} align="center" justify="start" mb="xl">
          <Title>Buy SKUs</Title>
          <Text size="lg" ta="center" fw={500} maw={580}>
            {intro}
          </Text>
          {caption.map((line) => (
            <Text key={line} size="sm" c="dimmed" ta="center" maw={520}>
              {line}
            </Text>
          ))}
        </Flex>
        <Flex
          align="center"
          justify="center"
          direction={{ base: "column", md: "row" }}
          gap={{ base: "1.5rem", md: 0 }}
        >
          <PlanBox
            style={{ borderRadius: "0.7rem 0 0 0.7rem" }}
            plan={packs[0]}
            onBuy={(done) =>
              startCheckout(checkoutSkuPack(packs[0].skuCount), done)
            }
          />
          <PlanBox
            plan={packs[1]}
            highlight
            onBuy={(done) =>
              startCheckout(checkoutSkuPack(packs[1].skuCount), done)
            }
          />
          <PlanBox
            style={{ borderRadius: "0 0.7rem 0.7rem 0" }}
            plan={packs[2]}
            onBuy={(done) =>
              startCheckout(checkoutSkuPack(packs[2].skuCount), done)
            }
          />
        </Flex>
        <Paper mt="xl" ta="center">
          <Text c="dimmed">
            You pay in {offer.payIn}. SKUs are added after payment. They last
            one year.
          </Text>
        </Paper>
        <Faq />
      </Stack>
    </Container>
  );
}

export const CreditsPricingPage = () => {
  const tenantProfile = useStore((state) => state.tenantProfile);
  const offer = skuOfferFromProfile(tenantProfile);
  if (offer) {
    return <OfferPacks offer={offer} />;
  }

  const usd100 = publicPackInCurrency("usd", 100);
  const usd500 = publicPackInCurrency("usd", 500);
  const usd1000 = publicPackInCurrency("usd", 1000);

  const package1 = {
    title: "100 SKUs",
    id: PUBLIC_PACK_CHECKOUT_IDS[100],
    features: [{ id: 1, text: `${usd100.perSku} per SKU` }],
    price: usd100.major,
    currencyMark: usd100.mark,
  };

  const package2 = {
    title: "500 SKUs",
    id: PUBLIC_PACK_CHECKOUT_IDS[500],
    features: [{ id: 10, text: `${usd500.perSku} per SKU` }],
    price: usd500.major,
    currencyMark: usd500.mark,
  };

  const package3 = {
    title: "1,000 SKUs",
    id: PUBLIC_PACK_CHECKOUT_IDS[1000],
    features: [{ id: 100, text: `${usd1000.perSku} per SKU` }],
    price: usd1000.major,
    currencyMark: usd1000.mark,
  };

  return (
    <Container size="xl">
      <Stack spacing={40} mt="md" mb="xl" pt="md" pb="xl">
        <a id="start" />
        <Flex
          direction="column"
          gap={10}
          align="center"
          justify="start"
          mb="xl"
        >
          <Title>Buy SKUs</Title>
          <Text size="lg" ta="center" fw={500}>
            One-time purchase. No subscription.
          </Text>
        </Flex>
        <Flex
          align="center"
          justify="center"
          direction={{ base: "column", md: "row" }}
          gap={{ base: "1.5rem", md: 0 }}
        >
          <PlanBox
            style={{ borderRadius: "0.7rem 0 0 0.7rem" }}
            plan={package1}
            onBuy={(done) =>
              startCheckout(
                checkoutCredits({ items: [{ quantity: 1, id: package1.id }] }),
                done,
              )
            }
          />
          <PlanBox
            plan={package2}
            highlight
            onBuy={(done) =>
              startCheckout(
                checkoutCredits({ items: [{ quantity: 1, id: package2.id }] }),
                done,
              )
            }
          />
          <PlanBox
            style={{ borderRadius: "0 0.7rem 0.7rem 0" }}
            plan={package3}
            onBuy={(done) =>
              startCheckout(
                checkoutCredits({ items: [{ quantity: 1, id: package3.id }] }),
                done,
              )
            }
          />
        </Flex>
        <Paper mt="xl" ta="center">
          <Text c="dimmed">
            SKUs are added immediately after purchase. They last one year.
          </Text>
        </Paper>
        <Paper mt="lg" ta="center">
          <Text c="dimmed">Prices are subject to change.</Text>
        </Paper>
        <Faq />
      </Stack>
    </Container>
  );
};
